import { motion } from "framer-motion";

/**
 * A small self-drawing building blueprint accent for the hero section.
 * Plays once on mount as a signature reveal.
 */
const EASE = [0.22, 1, 0.36, 1];

const draw = (delay, dur = 0.9) => ({
    initial: { pathLength: 0, opacity: 0 },
    animate: { pathLength: 1, opacity: 1 },
    transition: {
        pathLength: { duration: dur, ease: EASE, delay },
        opacity: { duration: 0.3, delay },
    },
});

const fade = (delay, dur = 0.6) => ({
    initial: { opacity: 0 },
    animate: { opacity: 1 },
    transition: { duration: dur, delay },
});

export default function HeroLineDraw({ className = "" }) {
    return (
        <div
            data-testid="hero-line-draw"
            className={`pointer-events-none ${className}`}
        >
            <svg viewBox="0 0 420 260" className="h-full w-full" preserveAspectRatio="xMidYMid meet">
                <defs>
                    <pattern id="hero-grid" width="20" height="20" patternUnits="userSpaceOnUse">
                        <path d="M20 0 L0 0 0 20" fill="none" stroke="rgba(212,255,46,0.08)" strokeWidth="0.5" />
                    </pattern>
                </defs>
                <rect width="420" height="260" fill="url(#hero-grid)" opacity="0.6" />

                {/* Ground */}
                <motion.line
                    x1="10" y1="230" x2="410" y2="230"
                    stroke="#d4ff2e" strokeWidth="1.2"
                    {...draw(1.6, 0.8)}
                />

                {/* Foundation slab */}
                <motion.rect
                    x="90" y="222" width="240" height="12"
                    fill="none" stroke="#d4ff2e" strokeWidth="1"
                    {...draw(2.0, 0.7)}
                />

                {/* Columns */}
                {[110, 175, 245, 310].map((x, i) => (
                    <motion.line
                        key={x}
                        x1={x} y1="222" x2={x} y2="80"
                        stroke="rgba(245,245,240,0.85)" strokeWidth="1"
                        {...draw(2.4 + i * 0.08, 0.75)}
                    />
                ))}

                {/* Floor slabs */}
                {[195, 165, 135, 105].map((y, i) => (
                    <motion.line
                        key={y}
                        x1="105" y1={y} x2="315" y2={y}
                        stroke="rgba(245,245,240,0.85)" strokeWidth="1"
                        {...draw(3.0 + i * 0.1, 0.6)}
                    />
                ))}

                {/* Roof */}
                <motion.polyline
                    points="90,80 210,40 330,80"
                    fill="none" stroke="rgba(245,245,240,0.9)" strokeWidth="1.2"
                    {...draw(3.7, 0.9)}
                />

                {/* Windows fade in */}
                <motion.g {...fade(4.2)}>
                    {[[120, 175], [155, 175], [190, 175], [225, 175], [260, 175], [295, 175]].map(([x, y], i) => (
                        <rect key={i} x={x} y={y} width="16" height="14" fill="rgba(212,255,46,0.14)" stroke="rgba(212,255,46,0.5)" strokeWidth="0.6" />
                    ))}
                    {[[120, 145], [155, 145], [190, 145], [225, 145], [260, 145], [295, 145]].map(([x, y], i) => (
                        <rect key={i} x={x} y={y} width="16" height="14" fill="rgba(212,255,46,0.14)" stroke="rgba(212,255,46,0.5)" strokeWidth="0.6" />
                    ))}
                    {[[120, 115], [155, 115], [190, 115], [225, 115], [260, 115], [295, 115]].map(([x, y], i) => (
                        <rect key={i} x={x} y={y} width="16" height="14" fill="rgba(212,255,46,0.14)" stroke="rgba(212,255,46,0.5)" strokeWidth="0.6" />
                    ))}
                </motion.g>

                {/* Roof crown accent */}
                <motion.rect
                    x="200" y="26" width="20" height="16"
                    fill="#d4ff2e"
                    initial={{ opacity: 0, scale: 0.6 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.6, delay: 4.6, ease: EASE }}
                    style={{ transformOrigin: "210px 34px" }}
                />

                {/* Annotations */}
                <motion.g {...fade(4.9)} fontFamily="JetBrains Mono, monospace" fontSize="8" fill="rgba(212,255,46,0.75)" letterSpacing="0.18em">
                    <text x="10" y="250">GL — 0.00m</text>
                    <text x="342" y="250">S1:500</text>
                    <text x="10" y="20">KL · DWG 001</text>
                </motion.g>

                {/* Ticks */}
                <g stroke="rgba(212,255,46,0.4)" strokeWidth="0.8" fill="none">
                    <path d="M5 5 L 15 5 M5 5 L5 15" />
                    <path d="M415 5 L405 5 M415 5 L415 15" />
                    <path d="M5 255 L15 255 M5 255 L5 245" />
                    <path d="M415 255 L405 255 M415 255 L415 245" />
                </g>
            </svg>
        </div>
    );
}
